import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { slotsApi } from "../../api";
import DietitianTopbar from "./DietitianTopbar";
import classes from "./Schedule.module.scss";

// ── Types ─────────────────────────────────────────────────
type SlotStatus = "booked" | "open" | "blocked" | "done";

type Slot = {
  id: string;
  date: string;
  time: string;
  timeVal: string;
  duration: number;
  status: SlotStatus;
  client?: string;
  clientId?: string;
  type?: string;
};

// ── Helpers ───────────────────────────────────────────────
const pad = (n: number) => String(n).padStart(2, "0");
const fmt = (d: Date)   => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const displayFmt = (iso: string) => {
  const [y, m, day] = iso.split("-").map(Number);
  return new Date(y, m - 1, day).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });
};
const today = fmt(new Date());
const todayLabel = new Date().toLocaleDateString("en-US", {
  weekday: "long", month: "long", day: "numeric", year: "numeric",
});
const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTH_NAMES = ["January","February","March","April","May","June","July","August","September","October","November","December"];

// Build calendar grid for a given year/month
const buildCalendar = (year: number, month: number): (string | null)[] => {
  const first = new Date(year, month, 1).getDay();
  const days  = new Date(year, month + 1, 0).getDate();
  const cells: (string | null)[] = Array(first).fill(null);
  for (let d = 1; d <= days; d++) {
    cells.push(`${year}-${pad(month + 1)}-${pad(d)}`);
  }
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
};

const statusLabel: Record<SlotStatus, string> = {
  booked: "Booked", open: "Open", blocked: "Blocked", done: "Done",
};

// ── Kebab menu ────────────────────────────────────────────
type MenuItem = { label: string; icon: React.ReactNode; danger?: boolean; onClick: () => void };

const KebabMenu = ({ items }: { items: MenuItem[] }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div className={classes.kebab_wrap} ref={ref}>
      <button
        className={classes.kebab_btn}
        onClick={() => setOpen(o => !o)}
        aria-label="Actions"
      >
        <span /><span /><span />
      </button>
      {open && (
        <div className={classes.kebab_menu}>
          {items.map((item, i) => (
            <button
              key={i}
              className={`${classes.kebab_item} ${item.danger ? classes.kebab_danger : ""}`}
              onClick={() => { item.onClick(); setOpen(false); }}
            >
              {item.icon}
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Spinner ───────────────────────────────────────────────
const Spinner = () => (
  <div className={classes.spinner_wrap}>
    <div className={classes.spinner} />
  </div>
);

// ── Component ─────────────────────────────────────────────
const Schedule = () => {
  const navigate = useNavigate();
  const [slots,        setSlots]       = useState<Slot[]>([]);
  const [slotDates,    setSlotDates]   = useState<Set<string>>(new Set());
  const [loading,      setLoading]     = useState(true);
  const [saving,       setSaving]      = useState(false);
  const [selectedDate, setSelDate]     = useState(today);
  const [showAdd,      setShowAdd]     = useState(false);
  const [slotFormErr,  setSlotFormErr] = useState("");
  const [slotForm,     setSlotForm]    = useState({
    time: "", duration: "45", status: "open" as SlotStatus, client: "", clientId: "", type: "New Client",
  });

  // Calendar month navigation
  const todayDate = new Date();
  const [calYear,  setCalYear]  = useState(todayDate.getFullYear());
  const [calMonth, setCalMonth] = useState(todayDate.getMonth());

  const prevMonth = () => {
    if (calMonth === 0) { setCalMonth(11); setCalYear(y => y - 1); }
    else setCalMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (calMonth === 11) { setCalMonth(0); setCalYear(y => y + 1); }
    else setCalMonth(m => m + 1);
  };

  const calCells = useMemo(() => buildCalendar(calYear, calMonth), [calYear, calMonth]);

  // ── Fetch slots for selected date ─────────────────────
  const fetchSlots = useCallback(async (date: string) => {
    setLoading(true);
    try {
      const data = await slotsApi.list(date);
      setSlots(data.slots ?? []);
    } catch (err) {
      console.error("Fetch slots error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  // ── Fetch all dates that have slots (calendar dots) ───
  const fetchDates = useCallback(async () => {
    try {
      const data = await slotsApi.dates();
      setSlotDates(new Set(data.dates ?? []));
    } catch (err) {
      console.error("Fetch slot dates error:", err);
    }
  }, []);

  useEffect(() => { fetchDates(); }, [fetchDates]);
  useEffect(() => { fetchSlots(selectedDate); }, [selectedDate, fetchSlots]);

  const daySlots = useMemo(() =>
    [...slots].sort((a, b) => a.timeVal.localeCompare(b.timeVal)),
    [slots]
  );

  const bookedCount = daySlots.filter(s => s.status === "booked").length;
  const openCount   = daySlots.filter(s => s.status === "open").length;

  // ── Mutations ─────────────────────────────────────────
  const removeSlot = async (id: string) => {
    setSaving(true);
    try {
      await slotsApi.remove(id);
      setSlots(prev => prev.filter(s => s.id !== id));
      // Refresh dates in case this was the last slot on that date
      fetchDates();
    } catch (err) {
      console.error("Remove slot error:", err);
    } finally {
      setSaving(false);
    }
  };

  const updateSlotStatus = async (id: string, status: SlotStatus) => {
    setSaving(true);
    try {
      await slotsApi.update(id, { status });
      setSlots(prev => prev.map(s => s.id === id ? { ...s, status } : s));
    } catch (err) {
      console.error("Update slot error:", err);
    } finally {
      setSaving(false);
    }
  };

  const markDone    = (id: string) => updateSlotStatus(id, "done");
  const markBooked  = (id: string) => updateSlotStatus(id, "booked");

  const cancelBooking = async (id: string) => {
    setSaving(true);
    try {
      await slotsApi.update(id, { status: "open", clientId: null, client: null, type: null });
      setSlots(prev => prev.map(s => s.id === id
        ? { ...s, status: "open", clientId: undefined, client: undefined, type: undefined }
        : s));
    } catch (err) {
      console.error("Cancel booking error:", err);
    } finally {
      setSaving(false);
    }
  };
  const toggleBlock = async (id: string) => {
    const slot = slots.find(s => s.id === id);
    if (!slot) return;
    const next: SlotStatus = slot.status === "blocked" ? "open" : "blocked";
    updateSlotStatus(id, next);
  };

  // Convert "HH:MM" (24h from <input type="time">) → "9:00 AM" display
  const to12h = (val: string) => {
    const [hStr, mStr] = val.split(":");
    let h = Number(hStr); const m = Number(mStr);
    const ap = h >= 12 ? "PM" : "AM";
    if (h === 0) h = 12;
    else if (h > 12) h -= 12;
    return `${h}:${pad(m)} ${ap}`;
  };

  const addSlot = async () => {
    if (!slotForm.time) return;
    const timeVal = slotForm.time; // already "HH:MM" from type="time"

    const duplicate = daySlots.some(s => s.timeVal === timeVal);
    if (duplicate) {
      setSlotFormErr(`A slot at ${to12h(timeVal)} already exists for this day.`);
      return;
    }
    setSlotFormErr("");

    setSaving(true);
    try {
      await slotsApi.create({
        date:     selectedDate,
        time:     to12h(slotForm.time),
        timeVal,
        duration: Number(slotForm.duration),
        status:   slotForm.status,
        client:   slotForm.client   || null,
        clientId: slotForm.clientId || null,
        type:     slotForm.type     || null,
      });
      setShowAdd(false);
      setSlotFormErr("");
      setSlotForm({ time: "", duration: "45", status: "open", client: "", clientId: "", type: "New Client" });
      // Refresh both slot list and calendar dots
      await Promise.all([fetchSlots(selectedDate), fetchDates()]);
    } catch (err) {
      console.error("Add slot error:", err);
    } finally {
      setSaving(false);
    }
  };

  const menuItems = (slot: Slot): MenuItem[] => {
    const items: MenuItem[] = [];

    if (slot.status === "booked") {
      items.push({
        label: "Mark as Done",
        icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>,
        onClick: () => markDone(slot.id),
      });
      items.push({
        label: "Cancel Booking",
        icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>,
        danger: true,
        onClick: () => cancelBooking(slot.id),
      });
    }
    if (slot.status === "done") {
      items.push({
        label: "Mark as Booked",
        icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>,
        onClick: () => markBooked(slot.id),
      });
    }
    if (slot.clientId) {
      items.push({
        label: "View Client",
        icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>,
        onClick: () => navigate(`/dietitian/clients/${slot.clientId}`),
      });
    }
    if (slot.status === "open" || slot.status === "blocked") {
      items.push({
        label: slot.status === "blocked" ? "Unblock Slot" : "Block Slot",
        icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d={slot.status === "blocked" ? "M7 11V7a5 5 0 0 1 10 0v4" : "M7 11V7a5 5 0 0 1 9.9-1"}/></svg>,
        onClick: () => toggleBlock(slot.id),
      });
    }
    items.push({
      label: "Remove Slot",
      icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/></svg>,
      danger: true,
      onClick: () => removeSlot(slot.id),
    });
    return items;
  };

  return (
    <>
      <DietitianTopbar
        title="Schedule"
        subtitle={todayLabel}
        actions={
          <button className={classes.add_slot_btn} onClick={() => setShowAdd(true)} disabled={saving}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
            Add Slot
          </button>
        }
      />

      <div className={classes.content}>
        <div className={classes.layout}>
          {/* ── Mini calendar ── */}
          <div className={classes.cal_wrap}>
            <div className={classes.cal_nav}>
              <button className={classes.cal_nav_btn} onClick={prevMonth}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M15 18l-6-6 6-6"/></svg>
              </button>
              <span className={classes.cal_month_label}>{MONTH_NAMES[calMonth]} {calYear}</span>
              <button className={classes.cal_nav_btn} onClick={nextMonth}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M9 18l6-6-6-6"/></svg>
              </button>
            </div>

            <div className={classes.cal_grid}>
              {DAY_NAMES.map(d => (
                <div key={d} className={classes.cal_dow}>{d}</div>
              ))}
              {calCells.map((iso, i) => {
                if (!iso) return <div key={`e-${i}`} />;
                const isToday = iso === today;
                const isSel   = iso === selectedDate;
                const hasDot  = slotDates.has(iso);
                const isPast  = iso < today;
                const dayNum  = Number(iso.split("-")[2]);
                return (
                  <button
                    key={iso}
                    className={`${classes.cal_day} ${isSel ? classes.cal_day_sel : ""} ${isToday && !isSel ? classes.cal_day_today : ""} ${isPast ? classes.cal_day_past : ""}`}
                    onClick={() => setSelDate(iso)}
                  >
                    {dayNum}
                    {hasDot && <span className={classes.cal_dot} />}
                  </button>
                );
              })}
            </div>

            {/* Legend */}
            <div className={classes.cal_legend}>
              <span className={classes.legend_item}><span className={classes.legend_dot_green} />Has slots</span>
              <span className={classes.legend_item}><span className={classes.legend_today_ring} />Today</span>
            </div>
          </div>

          {/* ── Slots panel ── */}
          <div className={classes.slots_col}>
            {/* Selected date header */}
            <div className={classes.slots_header}>
              <div>
                <div className={classes.slots_date}>
                  {displayFmt(selectedDate)}
                </div>
                <div className={classes.slots_meta}>{bookedCount} booked · {openCount} open</div>
              </div>
              <button className={classes.add_slot_sm} onClick={() => setShowAdd(true)}>+ Add Slot</button>
            </div>

            {/* Slot list */}
            <div className={classes.slot_list}>
              {loading ? <Spinner /> : (
                <>
                  {daySlots.length === 0 && (
                    <div className={classes.no_slots}>
                      No slots for this day.
                      <button className={classes.no_slots_btn} onClick={() => setShowAdd(true)}>Add one →</button>
                    </div>
                  )}
                  {daySlots.map(slot => (
                    <div key={slot.id} className={`${classes.slot_row} ${classes[`slot_${slot.status}`]}`}>
                      <div className={classes.slot_time}>{slot.time}</div>
                      <div className={`${classes.slot_dot} ${classes[`dot_${slot.status}`]}`} />
                      <div className={classes.slot_body}>
                        {slot.status === "booked" || slot.status === "done" ? (
                          <>
                            <div className={classes.slot_client}>{slot.client}</div>
                            <div className={classes.slot_type}>{slot.type} · {slot.duration} min</div>
                          </>
                        ) : (
                          <div className={classes.slot_open_label}>
                            {slot.status === "blocked" ? "Blocked" : `Open · ${slot.duration} min`}
                          </div>
                        )}
                      </div>
                      <span className={`${classes.slot_badge} ${classes[`badge_${slot.status}`]}`}>
                        {statusLabel[slot.status]}
                      </span>
                      <KebabMenu items={menuItems(slot)} />
                    </div>
                  ))}
                </>
              )}
            </div>
          </div>{/* end slots_col */}
        </div>{/* end layout */}
      </div>{/* end content */}

      {/* Add Slot Modal */}
      {showAdd && (
        <div className={classes.modal_overlay} onClick={() => { setShowAdd(false); setSlotFormErr(""); }}>
          <div className={classes.modal_box} onClick={e => e.stopPropagation()}>
            <div className={classes.modal_header}>
              <h3 className={classes.modal_title}>Add Slot — {displayFmt(selectedDate)}</h3>
              <button className={classes.modal_close} onClick={() => { setShowAdd(false); setSlotFormErr(""); }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <path d="M18 6 6 18M6 6l12 12"/>
                </svg>
              </button>
            </div>
            <div className={classes.form_body}>
              <div className={classes.form_row}>
                <div className={classes.field}>
                  <label className={classes.field_label}>Time</label>
                  <input className={classes.field_input} type="time"
                    value={slotForm.time} onChange={e => { setSlotFormErr(""); setSlotForm(p => ({ ...p, time: e.target.value })); }} />
                </div>
                <div className={classes.field}>
                  <label className={classes.field_label}>Slot Type</label>
                  <select className={classes.field_input} value={slotForm.type}
                    onChange={e => {
                      const t = e.target.value;
                      setSlotForm(p => ({ ...p, type: t, duration: t === "Follow-Up" ? "30" : "45" }));
                    }}>
                    <option value="New Client">New Client (45 min)</option>
                    <option value="Follow-Up">Follow-Up (30 min)</option>
                  </select>
                </div>
              </div>
              <div className={classes.field}>
                <label className={classes.field_label}>Status</label>
                <select className={classes.field_input} value={slotForm.status}
                  onChange={e => setSlotForm(p => ({ ...p, status: e.target.value as SlotStatus }))}>
                  <option value="open">Open</option>
                  <option value="booked">Booked</option>
                  <option value="blocked">Blocked</option>
                </select>
              </div>
              {slotForm.status === "booked" && (
                <div className={classes.field}>
                  <label className={classes.field_label}>Client Name</label>
                  <input className={classes.field_input} placeholder="e.g. Sarah Mansour"
                    value={slotForm.client} onChange={e => setSlotForm(p => ({ ...p, client: e.target.value }))} />
                </div>
              )}
            </div>
            {slotFormErr && (
              <p style={{ color: "#C0392B", fontSize: 12, margin: "0 0 8px", padding: "0 2px" }}>{slotFormErr}</p>
            )}
            <div className={classes.modal_actions}>
              <button className={classes.cancel_btn} onClick={() => { setShowAdd(false); setSlotFormErr(""); }} disabled={saving}>Cancel</button>
              <button className={classes.save_btn} onClick={addSlot} disabled={saving || !slotForm.time}>
                {saving ? <span className={classes.btn_spinner} /> : "Add Slot"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Schedule;
